import { SortParamValues } from 'src/providers/SortProvider'

import {
  GetProjectQuery,
  Order_By,
  ProjectView_Bool_Exp,
  ProjectView_Order_By
} from 'src/config/generated/graphql'
import { ParamValue } from 'src/hooks/useFilters'
import { ArrayElement } from 'src/types/common'
import { LandUseOptions, ProjectPurpose } from 'src/types/project'

import { PlantingAssetsData } from './assets'
import { camelToFlat } from './common'

export const mapAssets = (
  plantings: NonNullable<
    ArrayElement<
      ArrayElement<GetProjectQuery['Project']>['Applications']
    >['Plantings']
  >
): PlantingAssetsData => {
  return plantings.map(p => ({
    name: p.Name,
    plantingAssets: p.PlantingAssets.map(pa => ({
      id: pa.Id,
      uri: pa.Uri!
    })),
    monitoringEvents: p.MonitoringEvents.map(me => ({
      monitoredOn: me.MonitoredOn,
      monitoringAssets: me.MonitoringAssets.map(ma => ({
        id: ma.Id,
        uri: ma.Uri!
      }))
    }))
  }))
}

export function buildListProjectsWhere({
  filters,
  searchQuery
}: {
  filters?: { [key: string]: ParamValue | null }
  searchQuery?: string
  projectId?: string
}) {
  let where: ProjectView_Bool_Exp = {}

  if (filters?.season) {
    where.Applications = { SeasonId: { _eq: filters.season as string } }
  }

  if (filters?.region) {
    where.RegionId = { _eq: filters.region as string }
  }

  if (filters?.fundedProjects === 'funded') {
    where.TreesApproved = { _gt: 0 }
  }

  if (filters?.fundedProjects === 'visibleFunded') {
    where.TreesApproved = { _gt: 0 }
    where.PublicPage = { _eq: true }
  }

  if (filters?.fundedProjects === 'hiddenFunded') {
    where.TreesApproved = { _gt: 0 }
    where.PublicPage = { _eq: false }
  }

  if (filters?.fundedProjects === 'featuredProjects') {
    where.FeaturedOn = { _is_null: false }
  }

  if (filters?.notes === 'withNotes') {
    where.ProjectNotes = { Id: { _is_null: false } }
  }

  if (filters?.notes === 'withoutNotes') {
    where._not = { ProjectNotes: { Id: { _is_null: false } } }
  }

  if (filters?.purpose && filters?.purpose !== 'all') {
    where.Purpose = { _has_keys_any: [filters.purpose as string] }
  }

  if (searchQuery) {
    const ilikeQuery = `%${searchQuery}%`

    let or: ProjectView_Bool_Exp[] = [
      {
        Region: { Name: { _ilike: ilikeQuery } }
      },
      {
        Name: { _ilike: ilikeQuery }
      },
      {
        Planter: { Account: { Name: { _ilike: ilikeQuery } } }
      },
      {
        Planter: { Account: { Email: { _ilike: ilikeQuery } } }
      }
    ]

    if (Number.isInteger(parseInt(searchQuery))) {
      or = [...or, { Reference: { _eq: Number(searchQuery) } }]
    }

    where = { ...where, _or: or }
  }

  return where
}

export function buildListProjectsOrderBy(sort: SortParamValues) {
  let orderBy: ProjectView_Order_By = {
    CreatedOn: 'desc' as Order_By
  }

  if (!sort.sortColumn) return orderBy

  if (sort.sortColumn === 'Planter') {
    orderBy = { Planter: { Account: { Name: sort.sortOrder as Order_By } } }
  } else if (sort.sortColumn === 'Region') {
    orderBy = { Region: { Name: sort.sortOrder as Order_By } }
  } else if (sort.sortColumn === 'YearsFunded') {
    orderBy = { ProjectStats_aggregate: { count: sort.sortOrder as Order_By } }
  } else {
    orderBy = { [sort.sortColumn!]: sort.sortOrder }
  }

  return orderBy
}

export const getLandUseOptions = () => {
  return Object.values(LandUseOptions).map(landUse => ({
    value: landUse,
    label: camelToFlat(landUse)
  }))
}

export const getPurposeOptions = () => {
  return Object.values(ProjectPurpose).map(purpose => ({
    value: purpose,
    label: camelToFlat(purpose)
  }))
}
